import { ICartSession } from "../typings/interfaces/pos/cart-session";
import { CategoryResponse } from "../typings/interfaces/responses/category-response";
import { IBillItemLimit, countBillPieces, resolveBillItemLimit } from "./bill-item-limit";

/** Items that can't share a ticket with any other kind of item. */
export type MixedServiceKind = "PRESS_ONLY" | "CLEAN_ONLY" | "REDO";

export const MIXED_SERVICE_MESSAGES: Record<MixedServiceKind, string> = {
  PRESS_ONLY:
    "Press-only garments cannot be listed with any other service options.\n\nPlease remove all non press-only items to proceed!",
  CLEAN_ONLY:
    "Clean-only garments cannot be listed with any other service options.\n\nPlease remove all non clean-only items to proceed!",
  REDO: "Redo garments cannot be listed with any other service options.\n\nPlease remove all non redo items to proceed!",
};

/**
 * Press Only / Clean Only items can't be listed with other items on a ticket,
 * and nor can Redo items. Redo is checked separately: a redo item can also be
 * Press Only or Clean Only, so an all-redo ticket must still not mix the two.
 */
export const findMixedService = (session: ICartSession): MixedServiceKind | null => {
  const items = session.cartItems?.filter((item) => item.finalized) ?? [];
  if (items.length === 0) return null;
  const hasPressOnly = items.some((item) => item.pressOnly);
  const hasCleanOnly = items.some((item) => !item.pressOnly && item.cleanOnly);
  const hasStandard = items.some((item) => !item.pressOnly && !item.cleanOnly);
  const hasRedo = items.some((item) => item.redoDetails);
  const hasNonRedo = items.some((item) => !item.redoDetails);

  if (hasPressOnly && (hasCleanOnly || hasStandard)) return "PRESS_ONLY";
  if (hasCleanOnly && (hasPressOnly || hasStandard)) return "CLEAN_ONLY";
  if (hasRedo && hasNonRedo) return "REDO";
  return null;
};

export type CheckInBlocker =
  | { kind: "EMPTY" }
  | { kind: "UNFINISHED"; session: ICartSession }
  | { kind: "MIXED"; session: ICartSession; mixed: MixedServiceKind }
  | { kind: "OVER_LIMIT"; session: ICartSession; limit: IBillItemLimit };

/**
 * Why the cart can't be checked in yet, or null when it can. Checked in the
 * order the cashier would fix them: something on a ticket, every item
 * finished, no mixed services, each ticket within its piece limit. Edits are
 * exempt from the piece limit so an existing bill that is already over can
 * still be saved.
 */
export const findCheckInBlocker = (
  cart: ICartSession[],
  options: {
    editingBill: boolean;
    categories: CategoryResponse[] | undefined;
    defaultMaxItems: number | null | undefined;
  }
): CheckInBlocker | null => {
  if (!cart.some((session) => session.cartItems?.some((item) => item.finalized))) {
    return { kind: "EMPTY" };
  }

  const unfinished = cart.find((session) => session.cartItems?.some((item) => !item.finalized));
  if (unfinished) return { kind: "UNFINISHED", session: unfinished };

  for (const session of cart) {
    const mixed = findMixedService(session);
    if (mixed) return { kind: "MIXED", session, mixed };
  }

  if (!options.editingBill) {
    for (const session of cart) {
      const limit = resolveBillItemLimit(session, options.categories, options.defaultMaxItems);
      if (limit && countBillPieces(session) > limit.maxItems) {
        return { kind: "OVER_LIMIT", session, limit };
      }
    }
  }
  return null;
};

/** One-line reason shown on the disabled Check In button. */
export const checkInBlockerText = (blocker: CheckInBlocker): string => {
  switch (blocker.kind) {
    case "EMPTY":
      return "Add an item to check in";
    case "UNFINISHED":
      return "Finish the item being added (tap ✓) to check in";
    case "MIXED":
      return blocker.mixed === "PRESS_ONLY"
        ? "Press Only items can't be checked in with other items"
        : blocker.mixed === "CLEAN_ONLY"
        ? "Clean Only items can't be checked in with other items"
        : "Redo items can't be checked in with other items";
    case "OVER_LIMIT":
      return `A ${blocker.limit.serviceName ? `${blocker.limit.serviceName} ` : ""}ticket can hold ${
        blocker.limit.maxItems
      } piece${blocker.limit.maxItems === 1 ? "" : "s"}; this one has ${countBillPieces(blocker.session)}`;
  }
};
